/**
 * LobbyAlertResults
 *
 * Design: Crowd management dashboard
 * - Traffic light density indicator (Green/Yellow/Red)
 * - Occupancy trend analysis
 * - Visual density distribution
 */
import React from "react";
import { LobbyAlertResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  Users,
  AlertTriangle,
  ShieldCheck,
  Activity,
  BarChart3,
  TrendingUp,
} from "lucide-react";

interface LobbyAlertResultsProps {
  results: ResultsType;
}

export const LobbyAlertResults: React.FC<LobbyAlertResultsProps> = ({
  results,
}) => {
  const isVideo = results.type === "video";
  const densityLevel = isVideo
    ? results.overall_density_level || "normal"
    : results.density_level || "normal";

  const getDensityConfig = (level: string) => {
    switch (level.toLowerCase()) {
      case "high":
      case "critical":
        return {
          color: "text-red-400",
          bg: "from-red-500/20",
          border: "border-red-500/30",
          icon: <AlertTriangle className="w-10 h-10 text-white" />,
          label: "Critical Density",
          desc: "Immediate action required to reduce crowd",
          badge: "bg-red-500",
        };
      case "medium":
      case "moderate":
        return {
          color: "text-amber-400",
          bg: "from-amber-500/20",
          border: "border-amber-500/30",
          icon: <Activity className="w-10 h-10 text-white" />,
          label: "Moderate Density",
          desc: "Monitor situation closely",
          badge: "bg-amber-500",
        };
      default:
        return {
          color: "text-emerald-400",
          bg: "from-emerald-500/20",
          border: "border-emerald-500/30",
          icon: <ShieldCheck className="w-10 h-10 text-white" />,
          label: "Normal Density",
          desc: "Crowd levels are within safe limits",
          badge: "bg-emerald-500",
        };
    }
  };

  const config = getDensityConfig(densityLevel);
  const peopleCount = isVideo
    ? results.average_people_count || 0
    : results.people_count || 0;

  return (
    <div className="space-y-6">
      {/* Hero Density Card */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${config.bg} via-slate-800/50 to-slate-900 border ${config.border} p-8`}
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-500/5 rounded-full blur-3xl opacity-50" />

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div
              className={`w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-700/50 to-slate-800/50 border border-slate-600/30 flex items-center justify-center shadow-lg relative overflow-hidden`}
            >
              {/* Traffic Light Effect */}
              <div
                className={`absolute inset-0 opacity-20 ${config.badge}`}
              ></div>
              {config.icon}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider text-white ${config.badge} bg-opacity-20 border border-white/10`}
                >
                  {densityLevel}
                </span>
              </div>
              <h2 className={`text-4xl font-bold ${config.color} mb-1`}>
                {config.label}
              </h2>
              <p className="text-slate-400 text-sm">{config.desc}</p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center min-w-[120px]">
              <p className="text-3xl font-bold text-white">
                {formatNumber(peopleCount)}
              </p>
              <p className="text-slate-400 text-xs">People Detection</p>
            </div>
            {results.density_ratio !== undefined && (
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center min-w-[120px]">
                <p className="text-3xl font-bold text-primary-400">
                  {results.density_ratio.toFixed(2)}
                </p>
                <p className="text-slate-400 text-xs">Density Ratio</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Statistics Grid */}
      {isVideo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-slate-400 text-sm">Peak Occupancy</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {formatNumber(results.max_people_count || 0)}
            </p>
            <p className="text-slate-500 text-xs">
              Maximum people detected at once
            </p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-slate-400 text-sm">Average Occupancy</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {formatNumber(results.average_people_count || 0)}
            </p>
            <p className="text-slate-500 text-xs">Mean number of people</p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Activity className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-slate-400 text-sm">Minimum Occupancy</span>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {formatNumber(results.min_people_count || 0)}
            </p>
            <p className="text-slate-500 text-xs">Lowest count recorded</p>
          </div>
        </div>
      )}

      {/* Distribution Chart (Video Only) */}
      {isVideo && results.density_distribution && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Density Distribution</h3>
              <p className="text-slate-400 text-sm">Analysis over duration</p>
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(results.density_distribution || {}).map(
              ([level, count]) => {
                const totalFrames = Object.values(
                  results.density_distribution || {}
                ).reduce((a, b) => a + Number(b), 0);
                const percentage =
                  totalFrames > 0 ? (Number(count) / totalFrames) * 100 : 0;
                const conf = getDensityConfig(level);

                return (
                  <div key={level}>
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-sm font-medium capitalize ${conf.color.replace(
                          "text-",
                          "text-opacity-90 "
                        )}`}
                      >
                        {level}
                      </span>
                      <span className="text-slate-400 text-xs">
                        {percentage.toFixed(1)}% ({count} frames)
                      </span>
                    </div>
                    <div className="h-2.5 bg-slate-900 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${conf.badge}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}
    </div>
  );
};
